import React from 'react';
import { 
  Zap, 
  Book, 
  Brain, 
  FileText, 
  List, 
  Boxes, 
  History, 
  Ghost, 
  HelpCircle, 
  Satellite, 
  Terminal, 
  FileCode, 
  Compass, 
  Layers,
  Sparkles
} from 'lucide-react';

export type DocGroupId = 
  | 'first-steps' 
  | 'spatial-data-lake' 
  | 'tools-engine' 
  | 'memory-privacy' 
  | 'spatial-files' 
  | 'integrations';

export interface DocGroup {
  id: DocGroupId;
  label: {
    en: string;
    ar: string;
  };
}

export const docGroups: DocGroup[] = [
  {
    id: 'first-steps',
    label: {
      en: 'First Steps',
      ar: 'البداية والأساسيات'
    }
  },
  {
    id: 'spatial-data-lake',
    label: {
      en: 'Spatial Data Lake',
      ar: 'بحيرة البيانات المكانية'
    }
  },
  {
    id: 'tools-engine',
    label: {
      en: 'Spatial Tools & Engine',
      ar: 'محرك الأدوات والتشغيل'
    }
  },
  {
    id: 'memory-privacy',
    label: {
      en: 'Context & Memory',
      ar: 'السياق والذاكرة'
    }
  },
  {
    id: 'spatial-files',
    label: {
      en: 'Spatial Files & Research',
      ar: 'الملفات والأبحاث المكانية'
    }
  },
  {
    id: 'integrations',
    label: {
      en: 'Ecosystem & Integrations',
      ar: 'الربط المكتبي والمؤسسي'
    }
  }
];

export interface DocEntry {
  id: string;
  groupId: DocGroupId;
  title: {
    en: string;
    ar: string;
  };
  description: {
    en: string;
    ar: string;
  };
  href: string;
  keywords: string[];
  icon: React.ReactNode;
  category: 'core' | 'support' | 'advanced';
}

export const docsRegistry: DocEntry[] = [
  // ── GROUP 1: FIRST STEPS ──────────────────────────────────────────
  {
    id: 'getting-started',
    groupId: 'first-steps',
    title: { en: 'Getting Started', ar: 'البداية السريعة' },
    description: { 
      en: 'Initialize your workspace, configure strategic reasoning modes, explore the interactive map, and launch your first spatial analysis.', 
      ar: 'تهيئة مساحة العمل، اختيار أنماط التفكير الاستراتيجي، استكشاف الخريطة التفاعلية، وبدء أول تحليل مكاني.' 
    },
    href: '/nova/docs/getting-started',
    keywords: ['start', 'setup', 'welcome', 'initialize', 'modes', 'map', 'geojson', 'visualizer', 'بداية', 'تهيئة', 'مبتدئ', 'أنماط', 'خريطة', 'تحليل مكاني'],
    icon: <Zap size={16} />,
    category: 'core'
  },
  {
    id: 'prompt-guide',
    groupId: 'first-steps',
    title: { en: 'Strategic Reasoning & Prompting', ar: 'هندسة الأوامر وأنماط التفكير' },
    description: { 
      en: 'Master spatial prompt engineering for deterministic ArcPy, PyQGIS, and Google Earth Engine code generation.', 
      ar: 'أتقن صياغة الأوامر المكانية لتوليد أكواد ArcPy و GEE قطعية وعالية الدقة.' 
    },
    href: '/nova/docs/prompt-guide',
    keywords: ['prompt', 'python', 'arcpy', 'gee', 'logic', 'code', 'script', 'برمجة', 'كود', 'بايثون', 'أوامر'],
    icon: <Book size={16} />,
    category: 'core'
  },
  {
    id: 'faq',
    groupId: 'first-steps',
    title: { en: 'Frequently Asked Questions', ar: 'الأسئلة الشائعة' },
    description: { 
      en: 'Common questions regarding spatial data sovereignty, accuracy, execution speed, and pricing tiers.', 
      ar: 'أسئلة شائعة حول سيادة البيانات المكانية، دقة الإسقاط، سرعة التشغيل، والباقات.' 
    },
    href: '/nova/docs/faq',
    keywords: ['help', 'pricing', 'support', 'technical', 'accuracy', 'مساعدة', 'دعم', 'أسعار', 'دقة'],
    icon: <List size={16} />,
    category: 'support'
  },
  {
    id: 'changelog',
    groupId: 'first-steps',
    title: { en: 'Release Notes & Changelog', ar: 'سجل التحديثات والإصدارات' },
    description: { 
      en: 'Track the continuous evolution of VEXL Nova GeoAI engine versions, model capabilities, and dataset upgrades.', 
      ar: 'تتبع التطور المستمر لإصدارات محرك VEXL Nova، قدرات النماذج، وتحديثات البيانات المكانية.' 
    },
    href: '/nova/docs/changelog',
    keywords: ['updates', 'new', 'release', 'versions', 'v1.27.0', 'v1.26.0', 'v1.25.0', 'code interpreter', 'terminal', 'sandbox', 'تحديثات', 'إصدارات', 'جديد', 'محلل أكواد', 'تيرمينال'],
    icon: <FileText size={16} />,
    category: 'support'
  },

  // ── GROUP 2: SPATIAL DATA LAKE ────────────────────────────────────
  {
    id: 'spatial-data',
    groupId: 'spatial-data-lake',
    title: { en: 'Imagery Alignment & Data Lake', ar: 'تطابق الأقمار وبحيرة البيانات' },
    description: { 
      en: 'Sub-meter satellite photogrammetry alignment, geodetic datum corrections, and the 5 national vector layers.', 
      ar: 'تطابق الأقمار الصناعية عالي الدقة (0.5m)، تصحيح المراجع الجيوديسية، والطبقات الخمس لمصر.' 
    },
    href: '/nova/docs/spatial-data',
    keywords: ['spatial', 'data', 'lake', 'satellite', 'imagery', 'alignment', 'buildings', 'roads', 'geodesy', 'datum', 'helmert', 'بيانات', 'مكانية', 'أقمار_صناعية', 'مباني', 'طرق', 'جيوديسيا'],
    icon: <Satellite size={16} />,
    category: 'core'
  },

  // ── GROUP 3: SPATIAL TOOLS & ENGINE ───────────────────────────────
  {
    id: 'tool-library',
    groupId: 'tools-engine',
    title: { en: 'Spatial Tool Suite Overview', ar: 'نظرة عامة على أدوات نوفا' },
    description: { 
      en: 'Comprehensive directory of Nova specialized tools: GIS extractors, spatial calculators, and code generators.', 
      ar: 'دليل أدوات نوفا التخصصية: استخراج المعالم، الحسابات المكانية، وتوليد الأكواد.' 
    },
    href: '/nova/docs/tools',
    keywords: ['tools', 'functions', 'suite', 'library', 'أدوات', 'وظائف', 'مكتبة'],
    icon: <Boxes size={16} />,
    category: 'advanced'
  },
  {
    id: 'arcpy-execution',
    groupId: 'tools-engine',
    title: { en: 'Live ArcPy & PyQGIS Engine', ar: 'محرك تشغيل ArcPy و PyQGIS' },
    description: { 
      en: 'Deterministic ArcPy scripting, raster array memory optimization, and zero-runtime-error spatial environments.', 
      ar: 'كتابة سكربتات ArcPy قطعية، إدارة ذاكرة الراستر الكبيرة، وبيئة تشغيل معزولة خالية من الأخطاء.' 
    },
    href: '/nova/docs/arcpy-execution',
    keywords: ['arcpy', 'pyqgis', 'python', 'geoprocessing', 'raster', 'vector', 'code interpreter', 'sandbox', 'terminal', 'بايثون', 'أرك_باي', 'معالجة_مكانية', 'محلل_الأكواد', 'تيرمينال'],
    icon: <Terminal size={16} />,
    category: 'advanced'
  },
  {
    id: 'deep-research',
    groupId: 'tools-engine',
    title: { en: 'Geospatial Deep Research', ar: 'البحث الجغرافي والاستشهاد الأكاديمي' },
    description: { 
      en: 'Autonomous scientific literature extraction, verified citations from ArXiv and peer-reviewed journals, and DOI lookups.', 
      ar: 'استخراج الأبحاث المحكمة، توثيق المراجع الأكاديمية بنظام APA، والتحقق الآلي من معرفات DOI.' 
    },
    href: '/nova/docs/deep-research',
    keywords: ['research', 'papers', 'citations', 'literature', 'doi', 'academic', 'بحث', 'أوراق_علمية', 'مراجع'],
    icon: <Sparkles size={16} />,
    category: 'advanced'
  },
  {
    id: 'troubleshooting',
    groupId: 'tools-engine',
    title: { en: 'Hallucination Defense & Fixes', ar: 'استكشاف الأخطاء وتصحيح الهلوسة' },
    description: { 
      en: 'Zero-hallucination spatial verification protocols, coordinate reference debugging, and syntax recovery.', 
      ar: 'بروتوكولات منع الهلوسة المكانية، تصحيح أخطاء الإسقاط، ومعالجة أخطاء الأكواد.' 
    },
    href: '/nova/docs/troubleshooting',
    keywords: ['errors', 'bugs', 'hallucinations', 'fix', 'reliability', 'أخطاء', 'إصلاح', 'مشاكل'],
    icon: <HelpCircle size={16} />,
    category: 'support'
  },

  // ── GROUP 4: CONTEXT & MEMORY ─────────────────────────────────────
  {
    id: 'memory',
    groupId: 'memory-privacy',
    title: { en: 'Adaptive Cognitive Memory', ar: 'الذاكرة التكيفية' },
    description: { 
      en: 'How Nova retains your software versions, active project bounds, and preferred CRS across sessions automatically.', 
      ar: 'كيف تحفظ نوفا برمجياتك، نطاقات مشاريعك، وتفضيلات الإسقاط الجغرافي عبر الجلسات تلقائياً.' 
    },
    href: '/nova/docs/memory',
    keywords: ['memory', 'brain', 'persistence', 'facts', 'context', 'ذاكرة', 'استمرارية', 'سياق', 'تخصيص'],
    icon: <Brain size={16} />,
    category: 'core'
  },
  {
    id: 'managing-chats',
    groupId: 'memory-privacy',
    title: { en: 'Managing Chats & Zero-Trace', ar: 'إدارة المحادثات والحذف الجذري' },
    description: { 
      en: 'Organize spatial analysis sessions, search past scripts, and enforce Zero-Trace data destruction.', 
      ar: 'تنظيم مشاريع التحليل المكاني، البحث في الأكواد السابقة، والحذف الجذري للبيانات.' 
    },
    href: '/nova/docs/managing-chats',
    keywords: ['chats', 'history', 'delete', 'privacy', 'zero-trace', 'محادثات', 'حذف', 'خصوصية'],
    icon: <History size={16} />,
    category: 'core'
  },
  {
    id: 'temporary-chats',
    groupId: 'memory-privacy',
    title: { en: 'Incognito Temporary Chats', ar: 'المحادثات المؤقتة المعزولة' },
    description: { 
      en: 'Ephemeral sandboxed workspaces with zero database retention for sensitive spatial experiments.', 
      ar: 'مساحات عمل مؤقتة معزولة بدون أي تسجيل في قاعدة البيانات للمشاريع الجغرافية الحساسة.' 
    },
    href: '/nova/docs/temporary-chats',
    keywords: ['temporary', 'chats', 'incognito', 'privacy', 'sandbox', 'مؤقتة', 'محادثات', 'تصفح_مخفي'],
    icon: <Ghost size={16} />,
    category: 'core'
  },

  // ── GROUP 5: SPATIAL FILES & ARTIFACTS ────────────────────────────
  {
    id: 'spatial-files',
    groupId: 'spatial-files',
    title: { en: 'Spatial Files & GeoJSON Export', ar: 'الملفات المكانية وتصدير GeoJSON' },
    description: { 
      en: 'Export spatial query results to standard GeoJSON and ingest seamlessly into ArcGIS Pro and QGIS.', 
      ar: 'تصدير نتائج الاستعلامات بصيغة GeoJSON واستيرادها مباشرة داخل ArcGIS Pro و QGIS.' 
    },
    href: '/nova/docs/spatial-files',
    keywords: ['geojson', 'shapefile', 'export', 'gis', 'import', 'qgis', 'arcgis', 'تصدير', 'استيراد', 'خرائط'],
    icon: <FileCode size={16} />,
    category: 'core'
  },
  {
    id: 'docx-generator',
    groupId: 'spatial-files',
    title: { en: 'Academic DOCX Research Generator', ar: 'توليد أبحاث DOCX ومشاريع التخرج' },
    description: { 
      en: 'Automated generation of publication-ready Word documents formatted to academic thesis standards with formulas and references.', 
      ar: 'توليد تلقائي لملفات Word منسقة لمعايير رسائل الماجستير والتخرج مع المعادلات والمراجع.' 
    },
    href: '/nova/docs/docx-generator',
    keywords: ['docx', 'word', 'academic', 'thesis', 'graduation', 'report', 'أبحاث', 'تخرج', 'وورد', 'تقارير'],
    icon: <FileText size={16} />,
    category: 'core'
  },

  // ── GROUP 6: INTEGRATIONS ─────────────────────────────────────────
  {
    id: 'desktop-integration',
    groupId: 'integrations',
    title: { en: 'Desktop GIS Integration Bridge', ar: 'الربط المكتبي مع ArcGIS Pro و QGIS' },
    description: { 
      en: 'Connect Nova Desktop with local Python geoprocessing toolboxes (.atbx/.pyt) and local GPU workflows.', 
      ar: 'ربط تطبيق نوفا المكتبي مع أدوات المعالجة المحلية (.atbx/.pyt) ومعالجات كروت الشاشة المحلية.' 
    },
    href: '/nova/docs/desktop-integration',
    keywords: ['desktop', 'arcgis', 'qgis', 'plugin', 'atbx', 'pyt', 'bridge', 'ربط', 'ديسكتوب', 'أرك_برو'],
    icon: <Layers size={16} />,
    category: 'advanced'
  }
];
